/* eslint-disable react/jsx-no-useless-fragment */
import React, {
  useEffect,
  ChangeEvent,
  CSSProperties,
  ReactElement,
  useState,
  ReactNode,
} from 'react';
import _ from 'lodash';
import './style.scss';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DefaultTextField from 'components/Utils/DefaultTextField';
import { DefaultSwitch } from 'services/UIUtils';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import DefaultAutoComplete from 'components/Utils/DefaultAutoComplete';
import DialogActions from '@material-ui/core/DialogActions';
import DefaultButton from '../NewDefaultButton';
import { type IconSrc } from '../NewIcon';
import BookingConcernPicker from 'components/HealthRecords/Appointments/BookingConcernPicker';

export type BookingConcernSelectionValue = {
  concernId: string;
  optionId?: string;
  deliveryOption?: any;
};

export type ConfirmationFormItem<T extends string> =
  | {
      readonly type: 'textField';
      readonly label: T;
      readonly multiline?: boolean;
      readonly value?: string;
    }
  | { readonly type: 'switch'; readonly label: T; readonly value?: boolean }
  | {
      readonly type: 'select';
      readonly label: T;
      readonly inputs: string[];
      readonly inputElements?: ReactElement[];
      readonly value?: string;
    }
  | {
      readonly type: 'autocomplete';
      readonly label: T;
      readonly inputs: string[];
      readonly value?: string;
    }
  | {
      readonly type: 'booking-concern';
      readonly label: T;
      readonly patientId: string;
      readonly withDeliveryOptions?: boolean;
      readonly value?: BookingConcernSelectionValue;
    };

type FormItemSubmission<T extends ConfirmationFormItem<string>> = {
  [k in T['label']]?: ['switch'] extends [T['type']]
    ? boolean
    : ['booking-concern'] extends [T['type']]
    ? BookingConcernSelectionValue
    : string;
};

type FormItemSubmissionArray<T extends readonly ConfirmationFormItem<string>[]> =
  T extends readonly [
    infer S extends ConfirmationFormItem<string>,
    ...infer Rest extends ConfirmationFormItem<string>[],
  ]
    ? FormItemSubmission<S> & FormItemSubmissionArray<Rest>
    : {};

export type ConfirmationDialogProps<T extends readonly ConfirmationFormItem<string>[]> = {
  open?: boolean;
  title?: string;
  message?: ReactNode;
  onConfirm?: (
    event: React.MouseEvent<HTMLButtonElement>,
    data?: FormItemSubmissionArray<T>,
  ) => void;
  onInputValuesChanged?: (data: FormItemSubmissionArray<T>) => void;
  onCancel?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  confirmLabel?: string;
  cancelLabel?: string;
  loadingState?: boolean;
  additionalButton?: React.ReactNode;
  className?: string;
  confirmClassName?: string;
  cancelClassName?: string;
  confirmDisabled?: boolean;
  onClose?: () => void;
  confirmIcon?: IconSrc;

  // defaultInputValue?: string;
  // inputFieldLabel?: React.ReactNode;
  // inputFieldLabels?: { label: string; multiline?: string }[];
  // inputFieldLabelValues?: Partial<Record<string, any>>;
  // inputSelect?: any;
  // inputOptions?: any;
  // inputSwitch?: string;
  form?: T;
};

/**
 * ConfirmationDialog
 * this is an old version that's only used in the staff portal
 * @see ./wrapper - it contains a wrapper that uses a new version when targetting the patient portal
 */
function ConfirmationDialog<T extends readonly ConfirmationFormItem<string>[]>({
  title,
  open,
  message,
  onConfirm,
  onCancel,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  loadingState = false,
  additionalButton = null,
  className,
  confirmClassName = 'blue',
  cancelClassName = 'red',
  onInputValuesChanged,
  onClose,
  confirmIcon,
  confirmDisabled,
  form,
}: ConfirmationDialogProps<T>) {
  const [inputValues, setInputValues] = useState(
    _(form)
      .map((it) => [it.label, it.value])
      .fromPairs()
      .value() as FormItemSubmissionArray<T>,
  );

  const formLabels = form?.map((it) => it.label).join(',');
  useEffect(() => {
    if (!form) return;
    setInputValues((prev) => {
      const merged = { ...prev };
      let changed = false;
      form.forEach((it) => {
        if (!(it.label in merged) && it.value !== undefined) {
          (merged as any)[it.label] = it.value;
          changed = true;
        }
      });
      return changed ? merged : prev;
    });
  }, [formLabels]);

  useEffect(() => {
    if (inputValues && onInputValuesChanged) {
      onInputValuesChanged(inputValues);
    }
  }, [inputValues]);

  let style: CSSProperties = {};

  if (form && form.length > 0) {
    style.height = '500px';
  }

  function onClickConfirm(event: React.MouseEvent<HTMLButtonElement>) {
    onConfirm?.(event, inputValues);
  }

  function onChangeText(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setInputValues((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  }

  return (
    <Dialog
      open={!!open}
      onClose={onClose || onCancel || (onConfirm as any)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      style={style}
      className={className}
    >
      <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
      <DialogContent style={{ overflowX: 'hidden' }}>
        <DialogContentText id="alert-dialog-description">{message}</DialogContentText>
        {form?.map((it) => {
          switch (it.type) {
            case 'textField':
              return (
                <DefaultTextField
                  key={it.label}
                  name={it.label}
                  margin="dense"
                  id="input"
                  label={it.label}
                  autoFocus
                  fullWidth
                  multiline={it.multiline}
                  value={inputValues[it.label as keyof typeof inputValues]}
                  onChange={onChangeText}
                />
              );
            case 'switch':
              return (
                <DefaultSwitch
                  key={it.label}
                  name={it.label}
                  onChange={(event: ChangeEvent, isInputChecked: boolean) =>
                    setInputValues((prev) => ({
                      ...prev,
                      [it.label]: isInputChecked,
                    }))
                  }
                  label={it.label}
                  padded={false}
                  reversed
                  checked={!!inputValues[it.label as keyof typeof inputValues]}
                />
              );
            case 'select':
              return (
                <DefaultSelectField
                  label={it.label}
                  name={it.label}
                  style={{ width: '100%', display: 'inline-block' }}
                  selectedMenuItemStyle={{ color: '#2693d1' }}
                  inputs={it.inputs}
                  inputElements={it.inputElements}
                  value={inputValues[it.label as keyof typeof inputValues]}
                  onChange={onChangeText}
                />
              );
            case 'autocomplete':
              return (
                <DefaultAutoComplete
                  //@ts-ignore
                  id="input"
                  style={{ width: '100%' }}
                  placeholder={it.label}
                  input={{
                    value: inputValues[it.label as keyof typeof inputValues],
                    onChange: (value: string) =>
                      onChangeText({ target: { value, name: it.label } } as any),
                  }}
                  onUpdateInput={(value: string) =>
                    onChangeText({ target: { value, name: it.label } } as any)
                  }
                  dataSource={it.inputs}
                  popOut={true}
                />
              );
            case 'booking-concern': {
              const selection = inputValues[it.label as keyof typeof inputValues] as
                | BookingConcernSelectionValue
                | undefined;
              return (
                <BookingConcernPicker
                  key={it.label}
                  label={it.label}
                  patientId={it.patientId}
                  withDeliveryOptions={it.withDeliveryOptions}
                  value={selection?.concernId}
                  deliveryValue={selection?.deliveryOption?.type}
                  deliveryTelemedicineMethod={selection?.deliveryOption?.telemedicine_method}
                  onChange={(concernId, sel) =>
                    setInputValues((prev) => ({
                      ...prev,
                      [it.label]: {
                        concernId,
                        optionId: sel?.optionId,
                        deliveryOption: sel?.deliveryOption,
                      },
                    }))
                  }
                />
              );
            }
          }
        })}
      </DialogContent>
      <DialogActions>
        <DefaultButton
          name={cancelLabel}
          className={cancelClassName}
          id="alert-dialog-cancel"
          onClick={onCancel}
          disabled={loadingState}
          hide={onCancel === undefined}
          style={{ width: '150px', marginRight: '20px' }}
        />
        {additionalButton}
        <DefaultButton
          autoFocus={true}
          className={confirmClassName}
          name={confirmLabel}
          id="alert-dialog-confirm"
          disabled={confirmDisabled || loadingState}
          loading={loadingState}
          onClick={onClickConfirm}
          hide={onConfirm === undefined}
          icon={confirmIcon}
        />
      </DialogActions>
    </Dialog>
  );
}

export default ConfirmationDialog;
